/**
 * src/components/layout/AppLayout.jsx
 * Layout اصلی — همبرگر + Drawer از راست (RTL)
 * از AuthContext مستقیم استفاده می‌کند (نه useAuth hook)
 *
 * ── اصلاحات نسبت به نسخه‌های قبلی ──────────────────────────────────
 * 1) admin: "کاربر جدید" → /admin/users ، "چالش جدید" → /admin/challenges
 *    (چون ساخت اونا توی داشبورد ادمین modal بود، Route جدا نداشت)
 * 2) teacher: "دوره‌هایم" → /courses (Route ای به اسم /teacher/courses
 *    وجود نداشت)
 * 3) student: آیتم "تیکت‌های من" → /tickets اضافه شد
 * 4) یک بخش ثابت "🏠 اصلی" با آیتم "خانه" به‌عنوان اولین بخش برای هر
 *    ۴ نقش اضافه شد که با یک کلیک به داشبورد همون نقش برمی‌گردونه —
 *    آیتم‌های تکراری "داشبورد" قدیمی از داخل بخش‌های دیگه حذف شدن تا
 *    منو شلوغ نشه.
 */
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import ThemeToggle from "../common/ThemeToggle";

const DASHBOARD_BY_ROLE = {
  admin:   "/admin/dashboard",
  teacher: "/teacher/dashboard",
  support: "/support/dashboard",
  student: "/dashboard",
};

// بخش «خانه» — یکسان برای همه نقش‌ها، فقط مقصدش فرق می‌کنه
const HOME_SECTION = (role) => ({
  section: "🏠 اصلی",
  items: [
    { label: "خانه", icon: "🏠", path: DASHBOARD_BY_ROLE[role] || "/dashboard" },
  ],
});

// ── منوها بر اساس نقش — path های دقیق از AppRouter ─────────
const MENUS = {
  admin: [
    HOME_SECTION("admin"),
    { section: "⚡ ایجاد سریع", items: [
      { label:"جزوه جدید",         icon:"📖", path:"/notes/builder"              },
      { label:"دوره جدید",          icon:"📚", path:"/admin/course-builder"       },
      { label:"کاربر جدید",         icon:"👤", path:"/admin/users"                },
      { label:"آزمون جدید",         icon:"⚔️", path:"/quiz-builder"               },
      { label:"تکلیف جدید",         icon:"📋", path:"/assignments/builder"        },
      { label:"چالش جدید",          icon:"🎮", path:"/admin/challenges"           },
      { label:"واریزی‌ها",          icon:"💸", path:"/admin/wallet-requests"      },
      { label:"تیکتینگ",            icon:"🎫", path:"/ticketing"                  },
    ]},
    { section: "🗂️ مدیریت محتوا", items: [
      { label:"دوره‌ها",            icon:"📚", path:"/admin/courses"              },
      { label:"جزوه‌ها",            icon:"📖", path:"/notes/list"                 },
      { label:"آزمون‌ها",           icon:"⚔️", path:"/admin/quizzes"              },
      { label:"تکالیف",             icon:"📋", path:"/assignments/manage"         },
      { label:"چالش‌ها",            icon:"🎮", path:"/admin/challenges"           },
      { label:"بررسی آثار",         icon:"🏆", path:"/admin/showcase-review"      },
    ]},
    { section: "🗓️ زمان‌بندی", items: [
      { label:"مدیریت Slot‌ها",     icon:"🗓️", path:"/admin/slots"               },
      { label:"اختصاص ساپورت",      icon:"🔭", path:"/admin/assign-support"       },
      { label:"درخواست حذف Slot",   icon:"🗑️", path:"/admin/slot-delete-requests"},
      { label:"گزارش کلاس‌ها",      icon:"📊", path:"/admin/class-reports"        },
    ]},
    { section: "💰 مالی", items: [
      { label:"تسویه‌حساب",         icon:"💸", path:"/admin/settlement"           },
      { label:"کیف پول",            icon:"💰", path:"/admin/wallet"              },
      { label:"واریز",              icon:"💳", path:"/admin/wallet-deposits"      },
      { label:"گزارش مالی",         icon:"📈", path:"/admin/financial"            },
    ]},
    { section: "👥 کاربران", items: [
      { label:"کاربران",            icon:"👥", path:"/admin/users"                },
      { label:"HR",                 icon:"👔", path:"/admin/hr"                   },
      { label:"گزارش‌ها",          icon:"📊", path:"/admin/reports"              },
    ]},
    { section: "⚙️ حساب", items: [
      { label:"پروفایل",            icon:"👤", path:"/profile"                    },
      { label:"نوتیفیکیشن",         icon:"🔔", path:"/notifications"              },
      { label:"تنظیمات",            icon:"⚙️", path:"/settings"                  },
    ]},
  ],

  teacher: [
    HOME_SECTION("teacher"),
    { section: "📅 تدریس", items: [
      { label:"جلسات من",           icon:"📅", path:"/teacher/schedule"           },
      { label:"زمان‌بندی",          icon:"⏰", path:"/teacher/availability"       },
      { label:"دوره‌هایم",          icon:"📚", path:"/courses"                    },
      { label:"دانشجویان",          icon:"👥", path:"/teacher/students"           },
      { label:"کلاس‌های من",        icon:"🏫", path:"/teacher/classes"            },
    ]},
    { section: "📝 محتوا و ارزیابی", items: [
      { label:"ساخت جزوه",          icon:"📖", path:"/notes/builder"              },
      { label:"ساخت آزمون",         icon:"⚔️", path:"/teacher/quiz-builder"       },
      { label:"تصحیح آزمون",        icon:"✅", path:"/teacher/quiz-grading"       },
      { label:"تکالیف",             icon:"📋", path:"/teacher/assignments"        },
      { label:"بررسی آثار",         icon:"🏆", path:"/teacher/showcase-review"    },
      { label:"گزارش‌ها",          icon:"📊", path:"/teacher/reports"            },
    ]},
    { section: "💰 مالی", items: [
      { label:"حقوق",               icon:"💸", path:"/teacher/salary"             },
      { label:"گزارش حقوق",         icon:"📈", path:"/teacher/salary/report"      },
    ]},
    { section: "⚙️ حساب", items: [
      { label:"تیکتینگ",            icon:"🎫", path:"/ticketing"                  },
      { label:"تقویم",              icon:"📅", path:"/calendar"                   },
      { label:"پروفایل",            icon:"👤", path:"/profile"                    },
      { label:"نوتیفیکیشن",         icon:"🔔", path:"/notifications"              },
      { label:"تنظیمات",            icon:"⚙️", path:"/settings"                  },
    ]},
  ],

  support: [
    HOME_SECTION("support"),
    { section: "🔭 پشتیبانی", items: [
      { label:"مانیتور کلاس‌ها",    icon:"🔭", path:"/support/monitor"            },
      { label:"تقویم",              icon:"📅", path:"/support/calendar"           },
      { label:"دانشجویان",          icon:"👥", path:"/support/students"           },
      { label:"هشدارها",            icon:"⚠️", path:"/support/alerts"             },
    ]},
    { section: "⚙️ حساب", items: [
      { label:"تیکتینگ",            icon:"🎫", path:"/ticketing"                  },
      { label:"پروفایل",            icon:"👤", path:"/profile"                    },
      { label:"نوتیفیکیشن",         icon:"🔔", path:"/notifications"              },
      { label:"تنظیمات",            icon:"⚙️", path:"/settings"                  },
    ]},
  ],

  student: [
    HOME_SECTION("student"),
    { section: "📚 یادگیری", items: [
      { label:"جلسات من",           icon:"📅", path:"/my-classes"                 },
      { label:"دوره‌ها",            icon:"📚", path:"/courses"                    },
      { label:"جزوه‌ها",            icon:"📖", path:"/notes"                      },
      { label:"آزمون‌ها",           icon:"⚔️", path:"/quizzes"                    },
      { label:"تکالیف",             icon:"📋", path:"/assignments"                },
      { label:"چالش‌ها",            icon:"🎮", path:"/challenges"                 },
    ]},
    { section: "🌐 ارتباطات", items: [
      { label:"پیام‌ها",            icon:"💬", path:"/messages"                   },
      { label:"تیکت‌های من",        icon:"🎫", path:"/tickets"                    },
      { label:"تیکتینگ",            icon:"📨", path:"/ticketing"                  },
      { label:"گزارش‌ها",          icon:"📊", path:"/reports"                    },
    ]},
    { section: "⚙️ حساب", items: [
      { label:"کیف پول",            icon:"💰", path:"/wallet"                     },
      { label:"گواهینامه‌ها",       icon:"🏆", path:"/certificates"               },
      { label:"رزومه",              icon:"📄", path:"/resume"                     },
      { label:"تقویم",              icon:"📅", path:"/calendar"                   },
      { label:"پروفایل",            icon:"👤", path:"/profile"                    },
      { label:"نوتیفیکیشن",         icon:"🔔", path:"/notifications"              },
      { label:"تنظیمات",            icon:"⚙️", path:"/settings"                  },
    ]},
  ],
};

export default function AppLayout({ children }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const authCtx   = useContext(AuthContext);
  const user      = authCtx?.state?.user;
  const role      = user?.role || "student";
  const menus     = MENUS[role] || MENUS.student;

  const [open, setOpen] = useState(false);
  const drawerRef = useRef(null);

  // عرض drawer واکنش‌گرا — روی موبایل کمتر از 280px می‌شه
  const [drawerW, setDrawerW] = useState(
    typeof window !== "undefined" ? Math.min(280, window.innerWidth - 40) : 280
  );
  useEffect(() => {
    const handleResize = () => setDrawerW(Math.min(280, window.innerWidth - 40));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // بستن با کلیک بیرون
  useEffect(() => {
    const handle = (e) => {
      if (open && drawerRef.current && !drawerRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  // بستن با تغییر route
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // بستن با Escape
  useEffect(() => {
    const handle = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, []);

  const handleLogout = () => {
    ["access_token","refresh_token","cached_user"].forEach(k =>
      localStorage.removeItem(k)
    );
    if (authCtx?.dispatch) {
      authCtx.dispatch({ type: "AUTH_LOGOUT" });
    }
    navigate("/login");
  };

  return (
    <div style={{ minHeight:"100vh", position:"relative" }} dir="rtl">

      {/* ══ Topbar ══════════════════════════════════════════ */}
      <header style={{
        position:       "fixed",
        top: 0, right: 0, left: 0,
        zIndex:         1000,
        height:         52,
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        padding:        "0 16px",
        background:     "rgba(8, 10, 22, 0.97)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom:   "1px solid rgba(255,255,255,0.10)",
      }}>

        {/* همبرگر */}
        <button
          onClick={() => setOpen(p => !p)}
          aria-label="منو"
          style={{
            background:    "none",
            border:        "none",
            cursor:        "pointer",
            padding:       "8px 6px",
            display:       "flex",
            flexDirection: "column",
            gap:           5,
          }}>
          {[0,1,2].map(i => (
            <span key={i} style={{
              display:    "block",
              width:      22,
              height:     2.5,
              background: "rgba(255,255,255,0.92)",
              borderRadius: 2,
              transformOrigin: "center",
              transition:  "all 0.22s ease",
              transform: open
                ? i===0 ? "rotate(45deg) translate(5.5px, 5.5px)"
                : i===1 ? "scaleX(0)"
                : "rotate(-45deg) translate(5.5px, -5.5px)"
                : "none",
              opacity: open && i===1 ? 0 : 1,
            }} />
          ))}
        </button>

        {/* لوگو */}
        <button
          onClick={() => navigate(DASHBOARD_BY_ROLE[role] || "/dashboard")}
          style={{
            background:    "none",
            border:        "none",
            cursor:        "pointer",
            fontFamily:    "var(--font-pixel)",
            fontSize:      "var(--font-pixel-sm)",
            color:         "var(--color-accent)",
            letterSpacing: "0.04em",
          }}>
          BinaryBrains
        </button>

        {/* راست topbar */}
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <ThemeToggle />
          <button
            onClick={handleLogout}
            style={{
              background:   "none",
              border:       "1.5px solid var(--color-red)",
              borderRadius: "var(--radius-pill)",
              padding:      "5px 12px",
              cursor:       "pointer",
              fontFamily:   "var(--font-pixel)",
              fontSize:     "var(--font-pixel-xs)",
              color:        "var(--color-red)",
            }}>
            🚪 خروج
          </button>
        </div>
      </header>

      {/* ══ Overlay ═════════════════════════════════════════ */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position:      "fixed",
          inset:         0,
          zIndex:        1001,
          background:    "rgba(0,0,0,0.45)",
          opacity:       open ? 1 : 0,
          pointerEvents: open ? "all" : "none",
          transition:    "opacity 0.25s ease",
        }}
      />

      {/* ══ Drawer از راست ══════════════════════════════════ */}
      <nav
        ref={drawerRef}
        style={{
          position:      "fixed",
          top: 0,
          right:         open ? 0 : -drawerW,
          width:         drawerW,
          height:        "100vh",
          zIndex:        1002,
          background:    "rgba(8, 10, 22, 0.97)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderLeft:    "1px solid rgba(255,255,255,0.10)",
          boxShadow:     open ? "-8px 0 40px rgba(0,0,0,0.55)" : "none",
          transition:    "right 0.28s cubic-bezier(0.4,0,0.2,1)",
          overflowY:     "auto",
          display:       "flex",
          flexDirection: "column",
        }}>

        {/* هدر drawer */}
        <div style={{
          padding:      "14px 18px 12px",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          display:      "flex",
          alignItems:   "center",
          justifyContent:"space-between",
          position:     "sticky",
          top:          0,
          background:   "rgba(8, 10, 22, 0.97)",
          zIndex:       1,
        }}>
          <div>
            <div style={{
              fontFamily: "var(--font-pixel)",
              fontSize:   "var(--font-pixel-sm)",
              color:      "var(--color-accent)",
            }}>
              BinaryBrains
            </div>
            {user?.full_name && (
              <div style={{
                fontFamily: "var(--font-pixel)",
                fontSize:   "var(--font-pixel-xs)",
                color:      "rgba(255,255,255,0.45)",
                marginTop:  4,
              }}>
                👤 {user.full_name}
              </div>
            )}
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{
              background: "none", border:"none",
              cursor:     "pointer",
              color:      "rgba(255,255,255,0.45)",
              fontSize:   "1.1rem",
            }}>✕</button>
        </div>

        {/* آیتم‌های منو */}
        <div style={{ padding:"8px 0", flex:1 }}>
          {menus.map((section, si) => (
            <div key={si}>
              <div style={{
                fontFamily:    "var(--font-body)",
                fontSize:      "0.7rem",
                fontWeight:    700,
                color:         "rgba(255,255,255,0.40)",
                padding:       "14px 16px 4px",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                opacity:       0.7,
              }}>
                {section.section}
              </div>

              {section.items.map((item, ii) => {
                const isActive =
                  location.pathname === item.path ||
                  (item.path !== "/" && location.pathname.startsWith(item.path + "/"));
                return (
                  <button
                    key={ii}
                    onClick={() => navigate(item.path)}
                    style={{
                      width:         "100%",
                      display:       "flex",
                      alignItems:    "center",
                      gap:           10,
                      padding:       "9px 16px",
                      background:    isActive
                        ? "rgba(0,255,136,0.1)" : "transparent",
                      border:        "none",
                      borderRight:   `3px solid ${isActive ? "var(--color-accent)" : "transparent"}`,
                      cursor:        "pointer",
                      textAlign:     "right",
                      color:         isActive
                        ? "var(--color-accent)" : "rgba(255,255,255,0.80)",
                      transition:    "all 0.12s",
                      borderRadius:  "0 0 0 6px",
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                        e.currentTarget.style.color = "#ffffff";
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "rgba(255,255,255,0.80)";
                      }
                    }}>
                    <span style={{
                      fontSize:   "1rem",
                      flexShrink: 0,
                      width:      24,
                      textAlign:  "center",
                    }}>
                      {item.icon}
                    </span>
                    <span style={{
                      fontFamily: "var(--font-body)",
                      fontSize:   "0.88rem",
                      fontWeight: isActive ? 600 : 400,
                      lineHeight: 1.4,
                      letterSpacing: "0.01em",
                    }}>
                      {item.label}
                    </span>
                  </button>
                );
              })}

              {si < menus.length - 1 && (
                <div style={{
                  height:     1,
                  background: "rgba(255,255,255,0.08)",
                  margin:     "6px 14px",
                }} />
              )}
            </div>
          ))}
        </div>

        {/* پایین drawer */}
        <div style={{
          padding:   "14px 18px",
          borderTop: "1px solid rgba(255,255,255,0.10)",
        }}>
          <button
            onClick={handleLogout}
            style={{
              width:         "100%",
              padding:       "10px",
              background:    "rgba(255,68,68,0.08)",
              border:        "1.5px solid var(--color-red)",
              borderRadius:  "var(--radius-sm)",
              cursor:        "pointer",
              fontFamily:    "var(--font-pixel)",
              fontSize:      "var(--font-pixel-sm)",
              color:         "var(--color-red)",
              display:       "flex",
              alignItems:    "center",
              justifyContent:"center",
              gap:           8,
            }}>
            🚪 خروج از حساب
          </button>
        </div>
      </nav>

      {/* ══ محتوای صفحه ═════════════════════════════════════ */}
      <main style={{
        paddingTop: 52,
        minHeight:  "100vh",
        position:   "relative",
        zIndex:     1,
      }}>
        {children}
      </main>
    </div>
  );
}